'use client'

import { useTranslation } from '@shared/config/i18n'
import { toTutorProfileMediaUrl } from '@shared/libs/tutor-profile-media-url'
import Image from 'next/image'

import { Button, VStack } from '@podo-app/design-system-temp'

import type { LessonTutorSummary, NpsRating, NpsRatingVariant } from './nps-survey.types'
import { StarRating } from './star-rating'

interface NpsRatingStepProps {
  value: NpsRating | null
  onChange: (rating: NpsRating) => void
  onNext: () => void
  onSkip: () => void
  lessonTutorSummary?: LessonTutorSummary
  npsRatingVariant?: NpsRatingVariant
  onRecover?: () => void
}

const TITLE_BY_VARIANT = {
  legacy: 'lessonReview.nps.rating.title.legacy',
  'tutor-card': 'lessonReview.nps.rating.title.tutorCard',
} as const satisfies Record<NpsRatingVariant, string>

const NpsRatingTitle = ({ variant }: { variant: NpsRatingVariant }) => {
  const t = useTranslation()

  return (
    <VStack className="items-center gap-2 text-center">
      <span className="text-[20px] font-bold leading-7 tracking-[-0.4px] text-[#1C1C1C]">
        {t(TITLE_BY_VARIANT[variant])}
      </span>
      <div className="flex flex-col">
        <span className="text-[14px] font-medium leading-5 text-[#757575]">
          {t('lessonReview.nps.rating.descriptionLine1')}
        </span>
        <span className="text-[14px] font-medium leading-5 text-[#757575]">
          {t('lessonReview.nps.rating.descriptionLine2')}
        </span>
      </div>
    </VStack>
  )
}

const NpsRatingTutorCard = ({ tutorName, lessonDateTimeLabel, profileImageUrl }: LessonTutorSummary) => {
  const initial = tutorName.trim().charAt(0).toUpperCase() || '?'
  const imageUrl = toTutorProfileMediaUrl(profileImageUrl)

  return (
    <div
      className="flex h-[68px] w-[180px] items-center gap-3 rounded-xl border border-solid border-[#E7E7E7] bg-[#F8F8F8] px-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
      data-testid="nps-rating-tutor-card"
    >
      {imageUrl ? (
        <Image
          src={imageUrl}
          alt={tutorName}
          width={32}
          height={32}
          className="h-8 w-8 shrink-0 rounded-full object-cover"
          unoptimized
        />
      ) : (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#FF7A1A]">
          <span className="text-[13px] font-bold leading-4 text-white">{initial}</span>
        </div>
      )}
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-[15px] font-bold leading-5 text-[#1C1C1C]">{tutorName}</span>
        <span className="truncate text-[12px] font-medium leading-4 text-[#757575]">{lessonDateTimeLabel}</span>
      </div>
    </div>
  )
}

export const NpsRatingStep = ({
  value,
  onChange,
  onNext,
  onSkip,
  lessonTutorSummary,
  npsRatingVariant = 'legacy',
  onRecover,
}: NpsRatingStepProps) => {
  const t = useTranslation()

  if (npsRatingVariant === 'tutor-card' && lessonTutorSummary) {
    return (
      <VStack className="h-dvh" data-testid="nps-rating-step">
        <VStack className="flex-1 items-center justify-center gap-10 overflow-y-auto overflow-x-hidden scrollbar-hide [&::-webkit-scrollbar]:hidden p-5">
          <VStack className="items-center gap-5">
            <NpsRatingTitle variant="tutor-card" />
            <NpsRatingTutorCard {...lessonTutorSummary} />
          </VStack>

          <StarRating value={value} onChange={onChange} />
        </VStack>

        <VStack className="w-full shrink-0 gap-2 px-5 py-2">
          <Button size="xLarge" className="w-full" disabled={value === null} onClick={onNext}>
            {t('common.actions.next')}
          </Button>
          <button type="button" className="bg-transparent border-none cursor-pointer py-3" onClick={onSkip}>
            <span className="text-[14px] font-medium leading-5 text-[#A5A5A5]">
              {t('lessonReview.nps.actions.skip')}
            </span>
          </button>
          {onRecover && (
            <Button variant="ghost" onClick={onRecover}>
              {t('lessonReview.nps.actions.checkSaved')}
            </Button>
          )}
        </VStack>
      </VStack>
    )
  }

  return (
    <VStack className="h-dvh" data-testid="nps-rating-step">
      <VStack className="flex-1 items-center justify-center gap-10 overflow-y-auto overflow-x-hidden scrollbar-hide [&::-webkit-scrollbar]:hidden p-5">
        <NpsRatingTitle variant="legacy" />

        <StarRating value={value} onChange={onChange} />
      </VStack>

      <VStack className="w-full shrink-0 gap-2 px-5 py-2">
        <Button size="xLarge" className="w-full" disabled={value === null} onClick={onNext}>
          {t('common.actions.next')}
        </Button>
        <button type="button" className="bg-transparent border-none cursor-pointer py-3" onClick={onSkip}>
          <span className="text-[14px] font-medium leading-5 text-[#A5A5A5]">{t('lessonReview.nps.actions.skip')}</span>
        </button>
        {onRecover && (
          <Button variant="ghost" onClick={onRecover}>
            {t('lessonReview.nps.actions.checkSaved')}
          </Button>
        )}
      </VStack>
    </VStack>
  )
}
